<template>
  <sidebar-layout
    :show-leaderboard-ad="loading === 0"
  >
    <template>
      <loading-spinner v-if="loading !== 0" />
      <div v-else class="collection-page">
        <h1
          v-if="title"
          class="page-heading page-heading--underline"
          data-test-id="collection-page-heading"
        >{{ title }}</h1>
        <paginated-list
          :items="posts"
          :current-page="currentPage"
          :per-page="perPage"
          :total-count="totalCount"
        />
      </div>
    </template>
    <template v-slot:sidebar-ads>
      <sidebar-ads v-if="loading === 0" :sections="sidebarAdSections" />
    </template>
  </sidebar-layout>
</template>

<script>
import PaginatedList from '@/components/Pagination/PaginatedList.vue';
import SidebarAds from '@/components/Ads/SidebarAds.vue';
import routeMixin from '@/mixins/route-mixin';
import { config } from '@/configuration/nexus-config';
import { SEARCH_QUERY } from '@/graphql/queries/search-queries';
import { collectionJsonLd } from '@/utils/structured-data';

export default {
  name: 'CollectionComponent',
  components: {
    PaginatedList,
    SidebarAds,
  },
  mixins: [routeMixin],
  data() {
    return {
      loading: 0,
      totalCount: 0,
      perPage: 20,
      posts: [],
      dataLayer: {},
      sidebarAdSections: config.sidebarAdSections,
    };
  },
  metaInfo() {
    if (this.pageIsOnTwoOrMore) {
      return {
        title: this.$root.$options.filters.decodeHtml(this.$route.meta.title),
        meta: [
          {
            name: 'robots',
            vmid: 'robots',
            content: 'noindex',
          },
        ],
      };
    }
    if (!this.loading && this.posts) {
      return {
        title: this.$root.$options.filters.decodeHtml(this.$route.meta.title),
        meta: [
          {
            vmid: 'description',
            name: 'description',
            content: this.metaDescription,
          },
          {
            vmid: 'og:description',
            property: 'og:description',
            content: this.metaDescription,
          },
          {
            vmid: 'twitter:description',
            property: 'twitter:description',
            content: this.metaDescription,
          },
        ],
      };
    }
    return {};
  },
  computed: {
    title() {
      return this.$route.meta.title;
    },
    currentPage() {
      const pageFromRoute = this.$route.params.pageNumber || this.$route.query.page;
      return pageFromRoute ? Number(pageFromRoute) : 1;
    },
    tag() {
      return this.$route.params.slug || this.$route.meta.tag;
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage);
    },
    metaDescription() {
      const pageInfo = `page ${this.currentPage} of ${this.totalPages}`;
      const defaultMeta = this.$site.defaultMeta['metadesc-tax-category'] || '';
      return `${defaultMeta.replace('%%term_title%%', this.$route.meta.title)} ${pageInfo}`;
    },
  },
  watch: {
    posts(to) {
      if (!to) { return; }
      this.onLoad();
    },
  },
  methods: {
    setJsonLd() {
      this.$store.dispatch(
        'setContentJsonLd',
        collectionJsonLd(
          'Collection',
          this.totalCount,
          this.$route.path,
          this.$root.hostname,
          this.$site.settings.condition_name,
        ),
      );
    },
    onLoad() {
      this.setJsonLd();

      this.$store.dispatch('updateGlobalContext', {
        page: {
          type: 'Collection',
          id: `${this.tag}-${this.currentPage}`,
        },
        search: {
          type: 'Content Collection',
          query: this.posts.map((p) => p.id),
        },
      });
      this.$emit('loaded', this.dataLayer);
    },
  },
  apollo: {
    search: {
      query: SEARCH_QUERY,
      variables() {
        return {
          page: this.currentPage,
          query: '*',
          index: 'POST',
          searchSort: 'DATE',
          tagSlug: this.tag,
          limit: this.perPage,
        };
      },
      result({ data }) {
        if (data) {
          if (!process.browser) {
          // make sure that we set the jsonLD
          // for the page during SSR
            this.setJsonLd();
          }
          if (!data.search || !data.search.searchResults) { return; }
          this.totalCount = data.search.total;
          this.dataLayer = data.search.dataLayer;
          this.posts = data.search.searchResults;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_page-heading';
</style>
